import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule, Routes } from '@angular/router';
import { Subscription } from 'rxjs';
import { HelpChapter, HelpManual, HelpTerm, HelpTopic, HelpWorkflow } from '../guide.model';
import { guideInline, guideMarkdown } from '../guide-markdown';
import { GuideService } from '../guide.service';
import { HelpSearchHit } from '../guide-registry';

type Section =
    | { kind: 'intro' }
    | { kind: 'chapter'; chapter: HelpChapter }
    | { kind: 'all' }
    | { kind: 'walkthroughs' }
    | { kind: 'roles' }
    | { kind: 'glossary' };

/**
 * The Help centre: this module's manual and the "Getting around Spectrum"
 * manual, a chapter at a time, with the walkthroughs, the roles and the
 * glossary. `/help/:module?topic=<id>` opens a screen's page (the Guide's
 * "Read this page in the Help centre"); `?section=` opens a chapter, the
 * walkthroughs, roles or glossary, or `all` — the whole manual on one page,
 * which is also what printing gives.
 */
@Component({
    selector: 'spectrum-help-centre',
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './help-centre.component.html',
    styleUrl: './help-centre.component.scss'
})
export class SpectrumHelpCentreComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    readonly guide = inject(GuideService);

    manual: HelpManual = this.guide.registry.basics;
    section: Section = { kind: 'intro' };
    search = '';
    private subs = new Subscription();
    private htmlCache = new Map<string, string>();

    get manuals(): HelpManual[] {
        return this.guide.registry.manuals;
    }

    ngOnInit(): void {
        this.subs.add(this.route.paramMap.subscribe(() => this.pickManual()));
        this.subs.add(this.route.queryParamMap.subscribe(() => this.applyQuery()));
        this.guide.ensureManual().then(() => this.pickManual());
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    private pickManual(): void {
        const wanted = this.route.snapshot.paramMap.get('module');
        const registry = this.guide.registry;
        this.manual = registry.manualById(wanted) || registry.home || registry.basics;
        this.applyQuery();
    }

    private applyQuery(): void {
        const query = this.route.snapshot.queryParamMap;
        const topicId = query.get('topic');
        const wanted = query.get('section');
        const ref = this.guide.registry.topicById(topicId);
        if (ref && ref.manual === this.manual) {
            const chapter = this.manual.chapters.find(c => c.id === ref.topic.chapter);
            if (chapter) {
                this.section = { kind: 'chapter', chapter };
                setTimeout(() => this.scrollTo(ref.topic.id), 60);
                return;
            }
        }
        const chapter = this.manual.chapters.find(c => c.id === wanted);
        if (chapter) {
            this.section = { kind: 'chapter', chapter };
        } else if (wanted === 'walkthroughs' || wanted === 'roles' || wanted === 'glossary' || wanted === 'all') {
            this.section = { kind: wanted };
        } else {
            this.section = { kind: 'intro' };
        }
        this.scrollTop();
    }

    go(section: string): void {
        this.search = '';
        this.router.navigate(['/help', this.manual.module], { queryParams: { section } });
    }

    isOn(section: string): boolean {
        const s = this.section;
        return s.kind === 'chapter' ? s.chapter.id === section : s.kind === section;
    }

    topicsOf(chapter: HelpChapter): HelpTopic[] {
        return this.manual.topics.filter(t => t.chapter === chapter.id);
    }

    /** The chapters on show: one, or every chapter for the whole manual. */
    get shownChapters(): HelpChapter[] {
        const s = this.section;
        if (s.kind === 'chapter') {
            return [s.chapter];
        }
        return s.kind === 'all' ? this.manual.chapters : [];
    }

    get sortedGlossary(): HelpTerm[] {
        return [...this.manual.glossary].sort((a, b) => a.term.localeCompare(b.term));
    }

    get hits(): HelpSearchHit[] {
        return this.search.trim().length > 1 ? this.guide.registry.search(this.search, 20) : [];
    }

    openHit(hit: HelpSearchHit): void {
        this.search = '';
        this.router.navigate(['/help', hit.ref.manual.module], { queryParams: { topic: hit.ref.topic.id } });
    }

    openScreen(topic: HelpTopic): void {
        const ref = this.guide.registry.topicById(topic.id);
        if (ref) {
            this.guide.openTopic(ref);
            this.guide.show('screen');
        }
    }

    screenRoute(topic: HelpTopic): string | null {
        const ref = this.guide.registry.topicById(topic.id);
        return ref ? this.guide.topicRoute(ref) : null;
    }

    startWorkflow(workflow: HelpWorkflow): void {
        this.guide.startWorkflow(workflow.id);
    }

    topicTitle(id: string | undefined): string {
        return this.guide.registry.topicById(id)?.topic.title || '';
    }

    topicLink(id: string): { [key: string]: string } {
        return { topic: id };
    }

    /**
     * Prints the whole manual, or saves it as a PDF. The one-page view is
     * copied into a window of its own, since the app's frame (sidebar, header,
     * a content pane that scrolls) would otherwise print around it and cut it
     * to one screenful.
     */
    print(): void {
        this.search = '';
        const run = () => setTimeout(() => this.printDocument(), 150);
        if (this.section.kind !== 'all') {
            this.router.navigate(['/help', this.manual.module], { queryParams: { section: 'all' } }).then(run);
        } else {
            run();
        }
    }

    private printDocument(): void {
        const doc = document.querySelector('spectrum-help-centre .hc-doc');
        const popup = window.open('', '_blank');
        if (!doc || !popup) {
            window.print();
            return;
        }
        const title = guideInline(this.manual.title);
        popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title} — Spectrum user manual</title>`
            + `<style>${PRINT_CSS}</style></head><body><p class="kicker">Spectrum · user manual</p><h1>${title}</h1>${doc.innerHTML}</body></html>`);
        popup.document.close();
        popup.focus();
        setTimeout(() => popup.print(), 300);
    }

    scrollTo(id: string): void {
        document.getElementById(this.anchor(id))?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    anchor(id: string): string {
        return 'help-' + id.replace(/\./g, '-');
    }

    md(text: string): string {
        let html = this.htmlCache.get(text);
        if (html === undefined) {
            html = guideMarkdown(text);
            this.htmlCache.set(text, html);
        }
        return html;
    }

    line(text: string): string {
        const key = '\u0000' + text;
        let html = this.htmlCache.get(key);
        if (html === undefined) {
            html = guideInline(text);
            this.htmlCache.set(key, html);
        }
        return html;
    }

    /** The app's content pane scrolls, not the window; bring whichever does back to the top. */
    private scrollTop(): void {
        setTimeout(() => document.getElementById('spectrum-help-top')?.scrollIntoView({ block: 'start' }));
    }
}

/**
 * The Help centre's routes. Spread them into the app's signed-in children,
 * above any wildcard:
 *
 *     children: [ ...SPECTRUM_HELP_ROUTES, { path: 'dashboard', … } ]
 */
export const SPECTRUM_HELP_ROUTES: Routes = [
    { path: 'help', component: SpectrumHelpCentreComponent },
    { path: 'help/:module', component: SpectrumHelpCentreComponent },
];

/** The printed manual's look: a plain A4 document, the screen's buttons left out. */
const PRINT_CSS = `
    @page { size: A4; margin: 16mm 14mm; }
    body { font-family: Inter, system-ui, -apple-system, sans-serif; font-size: 11pt; line-height: 1.55; color: #0f172a; }
    .kicker { margin: 0; color: #64748b; font-size: 9pt; letter-spacing: .08em; text-transform: uppercase; }
    h1 { font-size: 24pt; margin: 2pt 0 12pt; }
    h2 { font-size: 16pt; margin: 18pt 0 4pt; break-after: avoid; }
    h2.hc-chapter { break-before: page; border-bottom: 2px solid #4f46e5; padding-bottom: 4pt; }
    h3 { font-size: 13pt; margin: 0 0 4pt; break-after: avoid; }
    h4 { font-size: 11pt; margin: 10pt 0 3pt; break-after: avoid; }
    button, .hc-mini-toc, .hc-hits { display: none !important; }
    .hc-topic { border-top: 1px solid #e2e8f0; padding: 10pt 0; }
    .hc-lede { color: #475569; }
    .hc-facts { display: grid; grid-template-columns: max-content 1fr; gap: 2pt 10pt; font-size: 10pt; }
    .hc-facts dt { font-weight: 700; color: #64748b; }
    .hc-facts dd { margin: 0; }
    .hc-terms dt { font-weight: 700; margin-top: 5pt; }
    .hc-terms dd { margin: 1pt 0 0; color: #334155; }
    .hc-result { background: #f0fdf4; padding: 4pt 7pt; border-radius: 4pt; }
    .hc-caution { border-left: 3px solid #d97706; background: #fffbeb; padding: 4pt 8pt; margin: 3pt 0; }
    .hc-muted { color: #64748b; }
    table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
    th, td { text-align: left; vertical-align: top; padding: 4pt 6pt; border-bottom: 1px solid #e2e8f0; }
    details summary { font-weight: 700; list-style: none; }
    details > div { padding-left: 12pt; }
    code { font-size: 9.5pt; background: #f1f5f9; padding: 0 3pt; border-radius: 3pt; }
    a { color: inherit; text-decoration: none; }
    li, .hc-topic dl, tr { break-inside: avoid; }
`;
